import * as PIXI from 'pixi.js';
import type { Token, HpTier } from '@machina/core';
import { TOKEN_SIZE_SQUARES } from '@machina/core';
import { setPixiTokenClicked } from './pixiTokenState';
import { useTokenImageStore } from '../stores/tokenImageStore';
import { useIsoSpriteStore } from '../stores/isoSpriteStore';
import { createFlameMesh, getFlameSkin, type FlameSkin } from './fireShader';
import { useMapStore } from '../stores/mapStore';

// ─── Condition icon data ────────────────────────────────────────────────────
/** SVG paths for condition glyphs (24x24 viewBox, same as Icon.tsx) */
const CONDITION_ICONS: Record<string, { label: string; color: number; path: string }> = {
  blinded:        { label: 'Blinded',        color: 0x78909c, path: 'M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z' },
  charmed:        { label: 'Charmed',        color: 0xe91e63, path: 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z' },
  deafened:       { label: 'Deafened',       color: 0x78909c, path: 'M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z' },
  frightened:     { label: 'Frightened',     color: 0xffc107, path: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' },
  grappled:       { label: 'Grappled',       color: 0x795548, path: 'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z' },
  incapacitated:  { label: 'Incapacitated',  color: 0x9c27b0, path: 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z' },
  invisible:      { label: 'Invisible',      color: 0x42a5f5, path: 'M12 2c-5.52 0-10 4.48-10 10v10l3-3 3 3 3-3 3 3 3-3 3 3V12c0-5.52-4.48-10-10-10zm-1 14H9v-2h2v2zm0-4H9V8h2v4zm4 4h-2v-2h2v2zm0-4h-2V8h2v4z' },
  paralyzed:      { label: 'Paralyzed',      color: 0xff5722, path: 'M22 11h-4.17l3.24-3.24-1.41-1.42L15 11h-2V9l4.66-4.66-1.42-1.41L13 6.17V2h-2v4.17L7.76 2.93 6.34 4.34 11 9v2H9L4.34 6.34 2.93 7.76 6.17 11H2v2h4.17l-3.24 3.24 1.41 1.42L9 13h2v2l-4.66 4.66 1.42 1.41L11 17.83V22h2v-4.17l3.24 3.24 1.42-1.41L13 15v-2h2l4.66 4.66 1.41-1.42L17.83 13H22v-2z' },
  petrified:      { label: 'Petrified',      color: 0x607d8b, path: 'M12 2L2 12l10 10 10-10L12 2zm0 2.83L19.17 12 12 19.17 4.83 12 12 4.83z' },
  poisoned:       { label: 'Poisoned',       color: 0x4caf50, path: 'M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2 0-2.34 1.95-5.44 6-9.14 4.05 3.7 6 6.79 6 9.14 0 3.63-2.65 6.2-6 6.2z' },
  prone:          { label: 'Prone',          color: 0x8d6e63, path: 'M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z' },
  restrained:     { label: 'Restrained',     color: 0x795548, path: 'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z' },
  stunned:        { label: 'Stunned',        color: 0xff9800, path: 'M7 2v11h3v9l7-12h-4l4-8z' },
  unconscious:    { label: 'Unconscious',    color: 0x37474f, path: 'M9 2c-1.05 0-2.05.16-3 .46 4.06 1.27 7 5.06 7 9.54 0 4.48-2.94 8.27-7 9.54.95.3 1.95.46 3 .46 5.52 0 10-4.48 10-10S14.52 2 9 2z' },
  concentrating:  { label: 'Concentrating',  color: 0x7c4dff, path: 'M12 2L2 12l10 10 10-10L12 2zm0 2.83L19.17 12 12 19.17 4.83 12 12 4.83z' },
  bloodied:       { label: 'Bloodied',       color: 0xf44336, path: 'M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8z' },
};

/** Pre-rendered condition icon textures — shared across all iso token renderers */
const conditionTexCache = new Map<string, PIXI.Texture>();
const ICON_SIZE = 32; // render at 2x for crisp display at 14-16px

function getConditionTex(svgPath: string): PIXI.Texture {
  const cached = conditionTexCache.get(svgPath);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = ICON_SIZE;
  canvas.height = ICON_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return PIXI.Texture.WHITE;

  const scale = ICON_SIZE / 24;
  ctx.scale(scale, scale);
  ctx.fillStyle = '#ffffff';
  ctx.fill(new Path2D(svgPath));

  const texture = PIXI.Texture.from(canvas);
  conditionTexCache.set(svgPath, texture);
  return texture;
}

/**
 * IsoTokenRenderer
 *
 * Renders tokens as billboarded sprites in the isometric view.
 * Tokens stand upright (not skewed with the ground) for the HD-2D look.
 *
 * Features:
 * - Idle bobbing animation (subtle up/down oscillation)
 * - Drop shadow on ground plane
 * - HP bar above token
 * - Depth sorting by iso Y position (further back = lower zIndex)
 * - Click-to-select with selection glow
 */

/** Check if a token is a placed light object (torch, lantern, etc.) */
function isLightObject(token: Token): boolean {
  return !!token.emitsLight && !token.combatantId && !token.isPlayerControlled;
}

const HP_TIER_COLORS: Record<HpTier, number> = {
  healthy: 0x4caf50,
  wounded: 0xffc107,
  bloody: 0xff5722,
  critical: 0xf44336,
  unconscious: 0x607d8b,
  dead: 0x424242,
};

// Idle bobbing config
const BOB_AMPLITUDE = 2;       // pixels up/down
const BOB_SPEED = 0.002;       // radians per ms
const SHADOW_ALPHA = 0.3;
const SHADOW_SCALE_Y = 0.35;   // squashed ellipse for iso shadow

export class IsoTokenRenderer {
  /** When true, torch labels are visible (GM + torch tool active). Set from IsoMapCanvas. */
  static torchLabelsVisible = false;

  public container: PIXI.Container;
  public readonly tokenId: string;

  // Visual elements
  private body: PIXI.Graphics;
  private imageSprite: PIXI.Sprite | null = null;
  private shadow: PIXI.Graphics;
  private hpBar: PIXI.Graphics;
  private selectionGlow: PIXI.Graphics;
  private nameLabel: PIXI.Text;
  private conditionContainer: PIXI.Container;
  private flameGraphic: PIXI.Graphics | null = null;
  private cageOverlay: PIXI.Graphics | null = null;

  // Fire shader (for torch/light objects)
  private fireMesh: PIXI.Mesh | null = null;
  private fireShader: PIXI.Shader | null = null;
  private flameSkin: FlameSkin | null = null;

  // State
  private token: Token;
  private tileWidth: number;
  private tileHeight: number;
  private bobPhase: number;
  private isSelected = false;
  private isDead = false;
  private prevConditionKey = '';

  // Walk animation state
  private walkTarget: { x: number; y: number } | null = null;
  private walkSpeed = 3; // pixels per frame
  // Drag cleanup — force-detach leaked event listeners
  private _dragCleanup: (() => void) | null = null;

  // Callbacks
  public onClick: ((tokenId: string, shiftKey: boolean) => void) | null = null;
  public onDragEnd: ((tokenId: string, x: number, y: number) => void) | null = null;

  constructor(token: Token, tileWidth: number, tileHeight: number) {
    this.token = token;
    this.tokenId = token.id;
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;
    this.bobPhase = Math.random() * Math.PI * 2; // randomize so tokens don't bob in sync

    this.container = new PIXI.Container();
    this.container.sortableChildren = true;

    // Shadow (on ground plane, behind token)
    this.shadow = new PIXI.Graphics();
    this.shadow.zIndex = 0;
    this.container.addChild(this.shadow);

    // Selection glow (behind body)
    this.selectionGlow = new PIXI.Graphics();
    this.selectionGlow.zIndex = 1;
    this.selectionGlow.visible = false;
    this.container.addChild(this.selectionGlow);

    // Token body shape
    this.body = new PIXI.Graphics();
    this.body.zIndex = 2;
    this.container.addChild(this.body);

    // Fire shader mesh (for torch/light objects) — above body
    if (isLightObject(token)) {
      // Resolve flame skin from current map environment
      const env = useMapStore.getState().activeMap?.environment;
      this.flameSkin = getFlameSkin(env);
      try {
        const r = this.tokenRadius;
        const flameHeight = r * 1.1; // 30% smaller
        const { mesh, shader } = createFlameMesh(flameHeight, 0.9, this.flameSkin);
        // Position flame at top of torch stick
        mesh.position.set(0, -r * 0.7);
        mesh.zIndex = 3;
        this.container.addChild(mesh);
        this.fireMesh = mesh;
        this.fireShader = shader;
      } catch (e) {
        // WebGL shader may fail — fall back to Graphics flame
        console.warn('[IsoToken] Fire shader failed, using fallback:', e);
        this.flameGraphic = new PIXI.Graphics();
        this.flameGraphic.zIndex = 3;
        this.container.addChild(this.flameGraphic);
      }
      // Cage tines overlay — drawn ON TOP of the flame (z=4)
      this.cageOverlay = new PIXI.Graphics();
      this.cageOverlay.zIndex = 4;
      this.cageOverlay.eventMode = 'none'; // don't eat pointer events
      this.container.addChild(this.cageOverlay);
    }

    // HP bar
    this.hpBar = new PIXI.Graphics();
    this.hpBar.zIndex = 3;
    this.container.addChild(this.hpBar);

    // Name label
    this.nameLabel = new PIXI.Text('', {
      fontFamily: 'Georgia, serif',
      fontSize: 11,
      fill: 0xffffff,
      stroke: 0x000000,
      strokeThickness: 2,
      align: 'center',
    });
    this.nameLabel.anchor.set(0.5, 0);
    this.nameLabel.zIndex = 4;
    this.container.addChild(this.nameLabel);

    // Condition icons — float above/around character head
    this.conditionContainer = new PIXI.Container();
    this.conditionContainer.zIndex = 5;
    this.conditionContainer.eventMode = 'static';
    this.conditionContainer.interactiveChildren = true;
    this.container.addChild(this.conditionContainer);
    this.prevConditionKey = token.conditions.map(c => c.type).join(',');

    // Interaction — click to select, drag to move
    // Use the container as the interactive target (body may get cleared)
    this.container.eventMode = 'static';
    this.container.cursor = 'pointer';
    const r = this.tokenRadius;
    // Hit area: tighter than before to avoid overlapping adjacent tokens.
    // Covers the body circle + some vertical space for the sprite.
    this.container.hitArea = new PIXI.Rectangle(-r * 1.5, -r * 3.5, r * 3.0, r * 4.5);

    let dragStart: { x: number; y: number } | null = null;
    let isDragging = false;
    // Track bound listeners so we can force-cleanup on destroy
    let activeMoveHandler: ((me: PIXI.FederatedPointerEvent) => void) | null = null;
    let activeUpHandler: (() => void) | null = null;

    const cleanup = () => {
      this.container.cursor = 'pointer';
      if (activeMoveHandler) {
        this.container.off('globalpointermove', activeMoveHandler);
        activeMoveHandler = null;
      }
      if (activeUpHandler) {
        this.container.off('pointerup', activeUpHandler);
        this.container.off('pointerupoutside', activeUpHandler);
        activeUpHandler = null;
      }
      dragStart = null;
      isDragging = false;
    };
    this._dragCleanup = cleanup;

    this.container.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
      // Clean up any leaked prior drag (defensive)
      cleanup();

      e.stopPropagation();
      setPixiTokenClicked(true);
      this.onClick?.(this.tokenId, e.shiftKey);
      dragStart = { x: e.global.x, y: e.global.y };
      isDragging = false;
      this.container.cursor = 'grabbing';

      const onMove = (me: PIXI.FederatedPointerEvent) => {
        if (!dragStart) return;
        const dx = me.global.x - dragStart.x;
        const dy = me.global.y - dragStart.y;
        // Only start dragging after 4px threshold to avoid accidental drags
        if (!isDragging && Math.sqrt(dx * dx + dy * dy) < 4) return;
        isDragging = true;
        // Move the container directly for visual feedback
        this.container.x += me.movementX / (this.container.parent?.scale.x ?? 1);
        this.container.y += me.movementY / (this.container.parent?.scale.y ?? 1);
        this.container.zIndex = Math.round(this.container.y);
      };

      const onUp = () => {
        if (isDragging) {
          // Convert final iso screen position back to top-down grid coords
          const finalX = this.container.x;
          const finalY = this.container.y;
          const halfW = this.tileWidth / 2;
          const halfH = this.tileHeight / 2;
          const gx = (finalX / halfW + finalY / halfH) / 2;
          const gy = (finalY / halfH - finalX / halfW) / 2;
          const topDownX = Math.round(gx) * 50;
          const topDownY = Math.round(gy) * 50;
          this.onDragEnd?.(this.tokenId, topDownX, topDownY);
        }
        cleanup();
      };

      activeMoveHandler = onMove;
      activeUpHandler = onUp;
      this.container.on('globalpointermove', onMove);
      this.container.on('pointerup', onUp);
      this.container.on('pointerupoutside', onUp);
    });

    // Initial draw
    this.drawAll();
    this.positionOnIsoGrid();
    if (!isLightObject(token)) {
      this.tryLoadImage();
    }
  }

  private get tokenRadius(): number {
    const size = TOKEN_SIZE_SQUARES[this.token.visuals?.size || 'medium'] || 1;
    return (this.tileWidth * size) / 2.5; // slightly smaller than tile for margin
  }

  private drawAll(): void {
    this.drawBody();
    this.drawShadow();
    this.drawHpBar();
    this.drawSelectionGlow();
    this.updateNameLabel();
    this.drawConditions();
  }

  private drawBody(): void {
    const g = this.body;
    g.clear();

    // Light objects (torches, lanterns) get a special render
    if (isLightObject(this.token)) {
      this.drawLightObject();
      return;
    }

    const r = this.tokenRadius;
    const colorStr = this.token.visuals?.color;
    const color = colorStr
      ? parseInt(colorStr.replace('#', ''), 16)
      : 0x6644aa;

    if (this.isDead) {
      // Dead: greyed out with X
      g.beginFill(0x424242, 0.5);
      g.drawCircle(0, 0, r);
      g.endFill();
      g.lineStyle(3, 0xff0000, 0.8);
      g.moveTo(-r * 0.5, -r * 0.5);
      g.lineTo(r * 0.5, r * 0.5);
      g.moveTo(r * 0.5, -r * 0.5);
      g.lineTo(-r * 0.5, r * 0.5);
      return;
    }

    // Token body — filled circle with border
    const hpTier = this.token.hpTier || 'healthy';
    const borderColor = HP_TIER_COLORS[hpTier] ?? 0x4caf50;

    g.lineStyle(2.5, borderColor, 1);
    g.beginFill(color, 0.9);
    g.drawCircle(0, 0, r);
    g.endFill();
  }

  /** Draw a gothic torch/lantern light object — ornamental stick + animated flame */
  private drawLightObject(): void {
    const g = this.body;
    const r = this.tokenRadius;
    const skin = this.flameSkin ?? getFlameSkin();

    g.lineStyle(0);

    // ─── Torch stick ─────────────────────────────────
    // Main shaft — tapers slightly
    g.beginFill(skin.stickColor, 1);
    g.moveTo(-2.5, r * 0.9);
    g.lineTo(-1.5, -r * 0.45);
    g.lineTo(1.5, -r * 0.45);
    g.lineTo(2.5, r * 0.9);
    g.closePath();
    g.endFill();

    // Leather wrap rings on shaft
    g.beginFill(skin.bracketColor, 0.5);
    g.drawRect(-3, r * 0.55, 6, 1.5);
    g.drawRect(-3, r * 0.35, 6, 1.5);
    g.drawRect(-3, r * 0.15, 6, 1.5);
    g.endFill();

    // Iron collar where cage meets stick (simple, not bulbous)
    g.beginFill(skin.bracketColor, 0.8);
    g.drawRect(-r * 0.25, -r * 0.48, r * 0.5, 3);
    g.endFill();

    // Draw cage tines on the overlay (above the flame)
    this.drawCageTines();

    // Base flame is drawn in drawFlame() which animates
    this.drawFlame(0);
  }

  /** Draw iron cage bars and spiky tines ON TOP of the flame */
  private drawCageTines(): void {
    if (!this.cageOverlay) return;
    const g = this.cageOverlay;
    g.clear();

    const r = this.tokenRadius;
    const skin = this.flameSkin ?? getFlameSkin();

    const cageBot = -r * 0.45;   // bottom of cage
    const cageTop = -r * 1.0;    // top of cage (taller to surround flame)
    const cageW = r * 0.3;       // half-width

    // Cage bars — vertical iron strips curving inward, surrounding the flame
    g.lineStyle(1.2, skin.bracketColor, 0.85);
    // Far left bar
    g.moveTo(-cageW, cageBot);
    g.bezierCurveTo(-cageW * 1.1, cageBot + (cageTop - cageBot) * 0.3,
                     -cageW * 0.6, cageBot + (cageTop - cageBot) * 0.7,
                     -cageW * 0.1, cageTop);
    // Far right bar
    g.moveTo(cageW, cageBot);
    g.bezierCurveTo(cageW * 1.1, cageBot + (cageTop - cageBot) * 0.3,
                     cageW * 0.6, cageBot + (cageTop - cageBot) * 0.7,
                     cageW * 0.1, cageTop);
    g.lineStyle(0);

    // Spiky tines — pointed spikes extending above the cage, flanking the flame
    g.beginFill(skin.bracketColor, 0.9);
    // Left outer spike
    g.moveTo(-cageW * 0.8, cageBot + (cageTop - cageBot) * 0.5);
    g.lineTo(-cageW * 0.9, cageTop - r * 0.12);
    g.lineTo(-cageW * 0.5, cageBot + (cageTop - cageBot) * 0.6);
    g.closePath();
    g.endFill();

    // Left inner spike (taller)
    g.beginFill(skin.bracketColor, 0.9);
    g.moveTo(-cageW * 0.35, cageTop + r * 0.02);
    g.lineTo(-cageW * 0.3, cageTop - r * 0.2);
    g.lineTo(-cageW * 0.05, cageTop - r * 0.02);
    g.closePath();
    g.endFill();

    // Right inner spike (taller)
    g.beginFill(skin.bracketColor, 0.9);
    g.moveTo(cageW * 0.35, cageTop + r * 0.02);
    g.lineTo(cageW * 0.3, cageTop - r * 0.2);
    g.lineTo(cageW * 0.05, cageTop - r * 0.02);
    g.closePath();
    g.endFill();

    // Right outer spike
    g.beginFill(skin.bracketColor, 0.9);
    g.moveTo(cageW * 0.8, cageBot + (cageTop - cageBot) * 0.5);
    g.lineTo(cageW * 0.9, cageTop - r * 0.12);
    g.lineTo(cageW * 0.5, cageBot + (cageTop - cageBot) * 0.6);
    g.closePath();
    g.endFill();

    // Rivet dots where bars meet the collar
    g.beginFill(0xccaa66, 0.5);
    g.drawCircle(-cageW * 0.8, cageBot + 2, 1);
    g.drawCircle(cageW * 0.8, cageBot + 2, 1);
    g.endFill();
  }

  /**
   * Draw animated pear-shaped flame with licking tongues.
   * Layered: outer glow → outer flame (pear) → licking tongues → inner flame → core.
   * Called from applyBob for per-frame animation.
   */
  private drawFlame(phase: number): void {
    if (!this.flameGraphic) return;
    const g = this.flameGraphic;
    g.clear();

    const r = this.tokenRadius * 0.7; // 30% smaller flames
    const skin = this.flameSkin ?? getFlameSkin();
    // Flame emits from top of orb cradle (going UP = negative Y)
    const baseY = -this.tokenRadius * 0.7;

    // Flickering — multiple frequencies for organic feel
    const s1 = Math.sin(phase * 7) * 0.10;
    const s2 = Math.sin(phase * 11) * 0.07;
    const s3 = Math.sin(phase * 17) * 0.04;
    const scale = 1 + s1 + s3;
    const lean = (s2 + s3) * 4;

    // ─── Layer 1: Outer glow (soft, large teardrop) ────────
    // Teardrop: narrow at base, expands to belly ~28% up, tapers to tip
    g.beginFill(skin.glowColor, 0.12);
    g.moveTo(-r * 0.12 + lean, baseY);                    // narrow base
    g.bezierCurveTo(
      -r * 0.6 * scale + lean, baseY - r * 0.12 * scale,  // rapid expansion near base
      -r * 0.62 * scale + lean, baseY - r * 0.4 * scale,   // widest belly (lower)
      lean, baseY - r * 1.35 * scale,                      // tapers to tip
    );
    g.bezierCurveTo(
      r * 0.62 * scale + lean, baseY - r * 0.4 * scale,
      r * 0.6 * scale + lean, baseY - r * 0.12 * scale,
      r * 0.12 + lean, baseY,
    );
    g.closePath();
    g.endFill();

    // ─── Layer 2: Outer flame — teardrop body (red/orange) ─
    // Narrow at base, widest ~28% up, long taper to pointed tip
    g.beginFill(skin.outerColor, 0.85);
    const baseHalf = r * 0.1 * scale; // narrow at fuel point
    g.moveTo(-baseHalf + lean, baseY);
    g.bezierCurveTo(
      -r * 0.45 * scale + lean, baseY - r * 0.1 * scale,   // rapid expansion near base
      -r * 0.48 * scale + lean, baseY - r * 0.32 * scale,   // widest belly (lower)
      -r * 0.18 * scale + lean, baseY - r * 0.85 * scale,   // narrowing toward tip
    );
    g.quadraticCurveTo(
      lean, baseY - r * 1.2 * scale,                        // sharp pointed tip
      r * 0.18 * scale + lean, baseY - r * 0.85 * scale,
    );
    g.bezierCurveTo(
      r * 0.48 * scale + lean, baseY - r * 0.32 * scale,
      r * 0.45 * scale + lean, baseY - r * 0.1 * scale,
      baseHalf + lean, baseY,
    );
    g.closePath();
    g.endFill();

    // ─── Layer 3: Licking tongues — wisps from the belly ──
    // Left tongue — emerges from the widest part (now lower)
    const lt = Math.sin(phase * 13) * 0.12;
    const ltLean = lean - r * 0.15 + Math.sin(phase * 19) * 2;
    g.beginFill(skin.outerColor, 0.55);
    g.moveTo(ltLean, baseY - r * 0.4 * scale);
    g.bezierCurveTo(
      ltLean - r * 0.12, baseY - r * (0.7 + lt) * scale,
      ltLean - r * 0.05, baseY - r * (0.9 + lt) * scale,
      ltLean - r * 0.01, baseY - r * (1.1 + lt) * scale,
    );
    g.bezierCurveTo(
      ltLean + r * 0.03, baseY - r * (0.9 + lt) * scale,
      ltLean + r * 0.06, baseY - r * 0.7 * scale,
      ltLean + r * 0.03, baseY - r * 0.4 * scale,
    );
    g.closePath();
    g.endFill();

    // Right tongue
    const rt = Math.sin(phase * 15 + 1.5) * 0.1;
    const rtLean = lean + r * 0.12 + Math.sin(phase * 21) * 2;
    g.beginFill(skin.outerColor, 0.5);
    g.moveTo(rtLean, baseY - r * 0.35 * scale);
    g.bezierCurveTo(
      rtLean + r * 0.1, baseY - r * (0.65 + rt) * scale,
      rtLean + r * 0.04, baseY - r * (0.85 + rt) * scale,
      rtLean + r * 0.01, baseY - r * (1.0 + rt) * scale,
    );
    g.bezierCurveTo(
      rtLean - r * 0.02, baseY - r * (0.85 + rt) * scale,
      rtLean - r * 0.06, baseY - r * 0.65 * scale,
      rtLean - r * 0.03, baseY - r * 0.35 * scale,
    );
    g.closePath();
    g.endFill();

    // Center tongue (tallest, thinnest — from the upper taper)
    const ct = Math.sin(phase * 9 + 0.7) * 0.08;
    g.beginFill(skin.innerColor, 0.45);
    g.moveTo(lean - r * 0.03, baseY - r * 0.75 * scale);
    g.bezierCurveTo(
      lean - r * 0.04, baseY - r * (1.0 + ct) * scale,
      lean - r * 0.01, baseY - r * (1.2 + ct) * scale,
      lean, baseY - r * (1.35 + ct) * scale,
    );
    g.bezierCurveTo(
      lean + r * 0.01, baseY - r * (1.2 + ct) * scale,
      lean + r * 0.04, baseY - r * (1.0 + ct) * scale,
      lean + r * 0.03, baseY - r * 0.75 * scale,
    );
    g.closePath();
    g.endFill();

    // ─── Layer 4: Inner flame — smaller teardrop (yellow) ──
    // Same teardrop: narrow base, belly lower, pointed tip
    g.beginFill(skin.innerColor, 0.9);
    const ibh = r * 0.06 * scale;
    g.moveTo(-ibh + lean, baseY);
    g.bezierCurveTo(
      -r * 0.26 * scale + lean, baseY - r * 0.08 * scale,   // expand fast near base
      -r * 0.28 * scale + lean, baseY - r * 0.25 * scale,   // belly lower
      -r * 0.12 * scale + lean, baseY - r * 0.55 * scale,
    );
    g.quadraticCurveTo(
      lean, baseY - r * 0.72 * scale,
      r * 0.12 * scale + lean, baseY - r * 0.55 * scale,
    );
    g.bezierCurveTo(
      r * 0.28 * scale + lean, baseY - r * 0.25 * scale,
      r * 0.26 * scale + lean, baseY - r * 0.08 * scale,
      ibh + lean, baseY,
    );
    g.closePath();
    g.endFill();

    // ─── Layer 5: White-hot core at base ──────────────────
    // Tiny bright point right at the fuel contact
    g.beginFill(skin.coreColor, 0.75);
    g.moveTo(-r * 0.04 + lean, baseY);
    g.bezierCurveTo(
      -r * 0.08 + lean, baseY - r * 0.06,
      lean - r * 0.04, baseY - r * 0.15,
      lean, baseY - r * 0.2,
    );
    g.bezierCurveTo(
      lean + r * 0.04, baseY - r * 0.15,
      r * 0.08 + lean, baseY - r * 0.06,
      r * 0.04 + lean, baseY,
    );
    g.closePath();
    g.endFill();
  }

  private drawShadow(): void {
    const g = this.shadow;
    g.clear();

    if (this.isDead) return;

    const r = this.tokenRadius;
    g.beginFill(0x000000, SHADOW_ALPHA);
    g.drawEllipse(0, r * 0.8, r * 0.9, r * SHADOW_SCALE_Y);
    g.endFill();
  }

  private drawHpBar(): void {
    const g = this.hpBar;
    g.clear();

    if (this.isDead || !this.token.visuals?.showHpBar || isLightObject(this.token)) return;

    const maxHp = this.token.maxHp || 1;
    const currentHp = this.token.currentHp ?? maxHp;
    const ratio = Math.max(0, Math.min(1, currentHp / maxHp));

    const r = this.tokenRadius;
    const barW = r * 1.8;
    const barH = 5;
    const barY = r + 18; // Below feet + name label

    // Background
    g.beginFill(0x000000, 0.6);
    g.drawRoundedRect(-barW / 2, barY, barW, barH, 2);
    g.endFill();

    // Fill
    const hpColor = ratio > 0.5 ? 0x4caf50 : ratio > 0.25 ? 0xffc107 : 0xf44336;
    g.beginFill(hpColor, 0.9);
    g.drawRoundedRect(-barW / 2, barY, barW * ratio, barH, 2);
    g.endFill();
  }

  private drawSelectionGlow(): void {
    const g = this.selectionGlow;
    g.clear();

    if (!this.isSelected) {
      g.visible = false;
      return;
    }

    g.visible = true;
    const r = this.tokenRadius + 6;

    // Isometric ellipse at feet — 2:1 ratio like the grid diamonds
    const ew = r * 1.4;    // wide
    const eh = r * 0.7;    // squashed
    const fy = r * 0.6;    // at foot level

    // Cyan iso selection ring
    g.lineStyle(2, 0x00e5ff, 0.8);
    g.drawEllipse(0, fy, ew, eh);
    // Outer glow
    g.lineStyle(4, 0x00e5ff, 0.15);
    g.drawEllipse(0, fy, ew + 3, eh + 2);
  }

  private updateNameLabel(): void {
    const name = this.token.name || '';
    // Torch labels: only visible to GM when torch tool is active
    if (isLightObject(this.token)) {
      this.nameLabel.visible = IsoTokenRenderer.torchLabelsVisible;
    }
    this.nameLabel.text = name;
    const r = this.tokenRadius;
    this.nameLabel.y = r + 2;
  }

  /** Position the container in isometric screen space */
  private positionOnIsoGrid(): void {
    const { x, y } = this.token.position;
    // Token positions are in top-down grid pixel coords.
    // Convert to iso screen coords.
    const gridSize = 50; // matches MapCanvas default
    const gx = x / gridSize;
    const gy = y / gridSize;

    const halfW = this.tileWidth / 2;
    const halfH = this.tileHeight / 2;

    this.container.x = (gx - gy) * halfW;
    this.container.y = (gx + gy) * halfH;

    // Depth sorting: higher Y in iso = further down screen = rendered on top
    this.container.zIndex = Math.round(this.container.y);
  }

  /**
   * Try to load a full-body iso sprite, falling back to circular token portrait.
   * Iso sprites are taller (not masked to circle) for the HD-2D billboarded look.
   * Also subscribes to isoSpriteStore so sprites appear when generated or facing changes.
   */
  private tryLoadImage(): void {
    // Priority: iso sprite (keyed by combatantId) > token portrait > tokenImageStore > nothing.
    // Each mob instance has its OWN sprite set — Fire Newt Warrior 1 and 2 look different.
    const spriteId = this.token.combatantId ?? this.token.id;
    const isoUrl = spriteId
      ? useIsoSpriteStore.getState().getCurrentSprite(spriteId)
      : undefined;
    const tokenUrl = this.token.visuals?.imageUrl
      || useTokenImageStore.getState().tokenImages[this.token.name];
    const url = isoUrl || tokenUrl;
    const isIsoSprite = !!isoUrl;

    console.log(`[IsoToken] ${this.token.name} (id=${spriteId?.substring(0, 8) ?? '?'}…): isoSprite=${!!isoUrl}, tokenUrl=${!!tokenUrl}, loading=${!!url}`);

    if (url) {
      this.loadSpriteUrl(url, isIsoSprite);
    }

    // Subscribe for sprite updates (generation completes, facing change)
    this.isoSpriteUnsub = useIsoSpriteStore.subscribe((state) => {
      if (!spriteId) return;
      const freshUrl = state.getCurrentSprite(spriteId);
      if (freshUrl && freshUrl !== this.currentSpriteUrl) {
        console.log(`[IsoToken] Sprite update for ${this.token.name} (facing: ${state.getFacing(spriteId)})`);
        this.loadSpriteUrl(freshUrl, true);
      }
    });
  }

  private isoSpriteUnsub: (() => void) | null = null;
  private currentSpriteUrl: string | null = null;

  /**
   * Chroma-key green screen removal.
   * FLUX generates sprites on bright green (#00FF00) backgrounds.
   * We key out pixels where green dominates over red and blue.
   * Soft feathered edge to avoid harsh cutout artifacts.
   */
  private static removeBg(img: HTMLImageElement): PIXI.Texture {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = imageData.data;

    for (let i = 0; i < d.length; i += 4) {
      const r = d[i];
      const g = d[i + 1];
      const b = d[i + 2];

      // How "green-screen" is this pixel?
      // Green must dominate both red and blue significantly
      const greenExcess = g - Math.max(r, b);
      const greenRatio = g / (r + g + b + 1); // +1 to avoid div/0

      if (greenExcess > 60 && greenRatio > 0.45) {
        // Strong green — fully transparent
        d[i + 3] = 0;
      } else if (greenExcess > 30 && greenRatio > 0.38) {
        // Moderate green — feathered edge
        const t = 1 - ((greenExcess - 30) / 30);
        d[i + 3] = Math.round(d[i + 3] * Math.max(0, Math.min(1, t)));
        // Also desaturate the green spill on edge pixels
        d[i + 1] = Math.round(g * t + Math.max(r, b) * (1 - t));
      }
      // else: keep original pixel
    }

    ctx.putImageData(imageData, 0, 0);
    return PIXI.Texture.from(canvas);
  }

  private loadSpriteUrl(url: string, isFullBody: boolean): void {
    this.currentSpriteUrl = url;

    // For full-body iso sprites, load via Image so we can strip the background
    if (isFullBody) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const tex = IsoTokenRenderer.removeBg(img);
        this.applySprite(tex, true);
      };
      img.onerror = () => {
        console.warn(`[IsoToken] Failed to load sprite image for ${this.token.name}`);
      };
      img.src = url;
      return;
    }

    // Circular token portrait — no bg removal needed
    PIXI.Texture.fromURL(url).then((tex) => {
      this.applySprite(tex, false);
    }).catch((err) => {
      console.warn(`[IsoToken] Failed to load sprite for ${this.token.name}:`, err);
    });
  }

  private applySprite(tex: PIXI.Texture, isFullBody: boolean): void {
    // Guard: renderer may have been destroyed while image was loading
    if (!this.body || this.body.destroyed) return;

    if (this.imageSprite) {
      this.body.removeChild(this.imageSprite);
      this.imageSprite.destroy({ texture: false });
      this.imageSprite = null;
    }
    if (this.imageMask) {
      this.body.removeChild(this.imageMask);
      this.imageMask.destroy();
      this.imageMask = null;
    }

    const spr = new PIXI.Sprite(tex);
    const r = this.tokenRadius;

    if (isFullBody) {
      const spriteHeight = r * 3.5;
      const aspectRatio = tex.width / tex.height;
      const spriteWidth = spriteHeight * aspectRatio;
      spr.width = spriteWidth;
      spr.height = spriteHeight;
      spr.anchor.set(0.5, 0.9);
      this.body.clear();
      // Explicit hit area so interaction works after clearing the graphics
      this.body.hitArea = new PIXI.Rectangle(-spriteWidth / 2, -spriteHeight * 0.9, spriteWidth, spriteHeight);
    } else {
      // Circular token portrait (fallback)
      const diameter = r * 2;
      spr.width = diameter;
      spr.height = diameter;
      spr.anchor.set(0.5, 0.5);

      const mask = new PIXI.Graphics();
      mask.beginFill(0xffffff);
      mask.drawCircle(0, 0, r);
      mask.endFill();
      this.body.addChild(mask);
      spr.mask = mask;
      this.imageMask = mask;
    }

    this.body.addChild(spr);
    this.imageSprite = spr;
  }

  private imageMask: PIXI.Graphics | null = null;

  /** Apply idle bobbing. Call every frame with elapsed ms. */
  applyBob(elapsedMs: number): void {
    if (this.isDead || this.walkTarget) return;

    this.bobPhase += BOB_SPEED * elapsedMs;

    // Animate fire shader for light objects
    if (isLightObject(this.token)) {
      if (this.fireShader) {
        this.fireShader.uniforms.uTime = this.bobPhase * 0.5; // slower = more majestic
      } else if (this.flameGraphic) {
        this.drawFlame(this.bobPhase);
      }
      return; // Light objects don't bob
    }

    const bobOffset = Math.sin(this.bobPhase) * BOB_AMPLITUDE;

    // Bob the body + glow + conditions, but keep name/HP grounded at feet
    this.body.y = bobOffset;
    this.selectionGlow.y = bobOffset;
    this.conditionContainer.y = bobOffset;

    // Individual condition badges also drift independently
    this.bobConditions(this.bobPhase);
  }

  /** Animate walking towards a target screen position. Returns true when arrived. */
  tickWalk(): boolean {
    if (!this.walkTarget) return true;

    const dx = this.walkTarget.x - this.container.x;
    const dy = this.walkTarget.y - this.container.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < this.walkSpeed) {
      this.container.x = this.walkTarget.x;
      this.container.y = this.walkTarget.y;
      this.walkTarget = null;
      return true;
    }

    this.container.x += (dx / dist) * this.walkSpeed;
    this.container.y += (dy / dist) * this.walkSpeed;
    this.container.zIndex = Math.round(this.container.y);
    return false;
  }

  /** Set a walk destination in iso screen coords */
  setWalkTarget(screenX: number, screenY: number): void {
    this.walkTarget = { x: screenX, y: screenY };
  }

  /** Update token data from store */
  update(token: Token): void {
    const posChanged = token.position.x !== this.token.position.x ||
                       token.position.y !== this.token.position.y;
    const hpChanged = token.currentHp !== this.token.currentHp ||
                      token.maxHp !== this.token.maxHp ||
                      token.hpTier !== this.token.hpTier;
    const selChanged = token.selected !== this.token.selected;
    const deadChanged = (token.hpTier === 'dead') !== this.isDead;
    const condKey = token.conditions.map(c => c.type).join(',');
    const condChanged = condKey !== this.prevConditionKey;

    this.token = token;
    this.isDead = token.hpTier === 'dead';
    this.isSelected = token.selected || false;

    if (posChanged) this.positionOnIsoGrid();
    if (hpChanged || deadChanged) {
      this.drawBody();
      this.drawShadow();
      this.drawHpBar();
    }
    if (selChanged) this.drawSelectionGlow();
    if (condChanged) {
      this.prevConditionKey = condKey;
      this.drawConditions();
    }
    if (deadChanged) this.drawAll();
  }

  /** Refresh torch label visibility (call when torch tool toggles) */
  refreshLabelVisibility(): void {
    if (isLightObject(this.token)) {
      this.nameLabel.visible = IsoTokenRenderer.torchLabelsVisible;
    }
  }

  setSelected(selected: boolean): void {
    if (this.isSelected === selected) return;
    this.isSelected = selected;
    this.drawSelectionGlow();
  }

  /**
   * Draw condition icons floating in a gentle arc around the character's head area.
   * Each badge is a small rounded-rect with a glyph icon, arranged in a semicircle
   * above the token so they feel like they're orbiting in space.
   */
  private drawConditions(): void {
    this.conditionContainer.removeChildren();

    if (this.isDead || isLightObject(this.token)) return;
    if (!this.token.conditions || this.token.conditions.length === 0) return;

    const r = this.tokenRadius;
    const badgeSize = 16;
    const maxBadges = 6;
    const conditions = this.token.conditions.slice(0, maxBadges);

    // Arc layout: semicircle above the head
    // Center of arc is above the sprite's head area
    const arcCenterY = -r * 2.8;  // above head (bumped up to clear sprite)
    const arcRadius = r * 1.2;     // how wide the arc spreads
    const arcStart = -Math.PI * 0.85;  // start angle (left side)
    const arcEnd = -Math.PI * 0.15;    // end angle (right side)

    conditions.forEach((condition, i) => {
      const info = CONDITION_ICONS[condition.type];
      const color = info?.color ?? 0xaaaaaa;
      const label = info?.label ?? condition.type;

      // Distribute evenly along the arc
      const t = conditions.length === 1
        ? 0.5
        : i / (conditions.length - 1);
      const angle = arcStart + (arcEnd - arcStart) * t;

      const bx = Math.cos(angle) * arcRadius;
      const by = arcCenterY + Math.sin(angle) * arcRadius * 0.5; // squash vertical

      const badge = new PIXI.Container();
      badge.x = bx;
      badge.y = by;

      // Background pill
      const bg = new PIXI.Graphics();
      bg.beginFill(color, 0.6);
      bg.drawRoundedRect(-badgeSize / 2, -badgeSize / 2, badgeSize, badgeSize, 4);
      bg.endFill();
      // Subtle border
      bg.lineStyle(1, 0x000000, 0.3);
      bg.drawRoundedRect(-badgeSize / 2, -badgeSize / 2, badgeSize, badgeSize, 4);
      badge.addChild(bg);

      // Icon glyph
      if (info?.path) {
        const tex = getConditionTex(info.path);
        const icon = new PIXI.Sprite(tex);
        const pad = 2;
        const iconSize = badgeSize - pad * 2;
        icon.width = iconSize;
        icon.height = iconSize;
        icon.anchor.set(0.5, 0.5);
        badge.addChild(icon);
      }

      // Tooltip on hover
      badge.eventMode = 'static';
      badge.cursor = 'pointer';
      badge.hitArea = new PIXI.Rectangle(-badgeSize / 2, -badgeSize / 2, badgeSize, badgeSize);

      const tip = new PIXI.Text(label, {
        fontFamily: 'Georgia, serif',
        fontSize: 9,
        fill: 0xf4e4bc,
        stroke: 0x000000,
        strokeThickness: 2,
        align: 'center',
      });
      tip.anchor.set(0.5, 1);
      tip.y = -badgeSize / 2 - 3;
      tip.visible = false;
      badge.addChild(tip);

      badge.on('pointerover', () => {
        tip.visible = true;
        bg.clear();
        bg.beginFill(color, 0.9);
        bg.drawRoundedRect(-badgeSize / 2, -badgeSize / 2, badgeSize, badgeSize, 4);
        bg.endFill();
      });
      badge.on('pointerout', () => {
        tip.visible = false;
        bg.clear();
        bg.beginFill(color, 0.6);
        bg.drawRoundedRect(-badgeSize / 2, -badgeSize / 2, badgeSize, badgeSize, 4);
        bg.endFill();
      });

      this.conditionContainer.addChild(badge);
    });

    // Overflow indicator
    if (this.token.conditions.length > maxBadges) {
      const moreText = new PIXI.Text(`+${this.token.conditions.length - maxBadges}`, {
        fontFamily: 'Georgia, serif',
        fontSize: 9,
        fill: 0xcccccc,
        fontWeight: 'bold',
      });
      moreText.anchor.set(0.5, 0.5);
      moreText.x = arcRadius + badgeSize;
      moreText.y = arcCenterY;
      this.conditionContainer.addChild(moreText);
    }
  }

  /**
   * Animate condition badges with a gentle floating drift.
   * Called from applyBob each frame. Badges drift slightly up/down
   * with staggered timing so each one moves independently.
   */
  bobConditions(phase: number): void {
    const badges = this.conditionContainer.children;
    for (let i = 0; i < badges.length; i++) {
      const badge = badges[i];
      if (badge instanceof PIXI.Text) continue; // skip overflow indicator
      const offset = Math.sin(phase * 1.5 + i * 1.2) * 1.5;
      // Store the base Y the first time, then drift from it
      badge.y = (badge as PIXI.Container & { _baseY?: number })._baseY
        ? (badge as PIXI.Container & { _baseY?: number })._baseY! + offset
        : badge.y;
      if (!(badge as PIXI.Container & { _baseY?: number })._baseY) {
        (badge as PIXI.Container & { _baseY?: number })._baseY = badge.y;
      }
    }
  }

  destroy(): void {
    this._dragCleanup?.();
    this.isoSpriteUnsub?.();
    if (this.imageSprite) {
      this.imageSprite.destroy({ texture: false });
    }
    if (this.imageMask) {
      this.imageMask.destroy();
    }
    if (this.fireMesh) {
      this.fireMesh.destroy();
    }
    this.container.destroy({ children: true });
  }
}
